import React, { useEffect, useState } from 'react';
import { Loader2, Send, AlertCircle, CheckCircle2 } from 'lucide-react';
import { atlasApi, AtlasUser, FormularioParaResponder, AvaliacaoCompetencia, RespostaItem } from '../shared/atlasApi';

interface Props {
  user: AtlasUser;
  cicloId: string;
  avaliadoFunccodi: number;
  onConcluir: () => void;
}

const TIPO_LABEL: Record<string, string> = {
  auto: 'Autoavaliação', gestor: 'Avaliação como gestor(a)', par: 'Avaliação como par', liderado: 'Avaliação como liderado(a)',
};

interface ValorItem { nota?: number; comentario?: string }

const Responder: React.FC<Props> = ({ user, cicloId, avaliadoFunccodi, onConcluir }) => {
  const [dados, setDados] = useState<FormularioParaResponder | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [jaRespondida, setJaRespondida] = useState(false);
  const [valores, setValores] = useState<Record<string, ValorItem>>({});

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    atlasApi.getFormularioResponder(user, cicloId, avaliadoFunccodi)
      .then(r => { if (!cancel) setDados(r); })
      .catch(e => { if (!cancel) setErro(e.message || 'Erro ao carregar'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user, cicloId, avaliadoFunccodi]);

  const setVal = (cid: string, patch: ValorItem) => setValores(p => ({ ...p, [cid]: { ...p[cid], ...patch } }));

  const enviar = async () => {
    if (!dados) return;
    const comps = dados.formulario.competencias || [];
    // Validação: competências de nota/escolha precisam de valor; texto usa o comentário.
    const faltando = comps.some(c => {
      const v = valores[c.id] || {};
      if (c.tipo === 'texto') return !v.comentario || !v.comentario.trim();
      return v.nota === undefined || v.nota === null;
    });
    if (faltando) { setErro('Responda todas as competências.'); return; }
    setSalvando(true);
    setErro(null);
    const respostas: RespostaItem[] = comps.map(c => {
      const v = valores[c.id] || {};
      const item: RespostaItem = { competencia_id: c.id };
      if (c.tipo !== 'texto' && v.nota != null) item.nota = v.nota;
      if (v.comentario && v.comentario.trim()) item.comentario = v.comentario.trim();
      return item;
    });
    try {
      await atlasApi.enviarResposta(user, { ciclo_id: cicloId, avaliado_funccodi: avaliadoFunccodi, tipo_avaliacao: dados.tipo_avaliacao, respostas });
      onConcluir();
    } catch (e: any) {
      if (e?.status === 409) { setJaRespondida(true); }
      else { setErro(e.message || 'Erro ao enviar.'); setSalvando(false); }
    }
  };

  if (loading) return <div className="p-10 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...</div>;
  if (erro && !dados) return <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-700 max-w-2xl">{erro}</div>;
  if (!dados) return null;

  if (jaRespondida) {
    return (
      <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-gray-200 p-8 text-center">
        <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-gray-900">Avaliação já respondida</h2>
        <p className="text-sm text-gray-500 mt-1">Você já enviou esta avaliação anteriormente.</p>
        <button onClick={onConcluir} className="mt-4 px-5 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium">Voltar</button>
      </div>
    );
  }

  const auto = dados.tipo_avaliacao === 'auto';
  const comps = dados.formulario.competencias || [];

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">{TIPO_LABEL[dados.tipo_avaliacao] || dados.tipo_avaliacao} · {dados.ciclo.nome}</div>
        <h2 className="text-lg font-bold text-gray-900">{auto ? 'Autoavaliação' : (dados.avaliado.nome || `Colaborador #${dados.avaliado.funccodi}`)}</h2>
        {dados.avaliado.cargo && <p className="text-sm text-gray-500">{dados.avaliado.cargo}</p>}
      </div>

      {comps.map((c: AvaliacaoCompetencia) => {
        const v = valores[c.id] || {};
        return (
          <div key={c.id} className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-800">{c.nome}</div>
                {c.descricao && <div className="text-xs text-gray-400 mt-0.5">{c.descricao}</div>}
              </div>
              {c.peso != null && <span className="text-[10px] text-gray-400 uppercase tracking-wider">peso {c.peso}</span>}
            </div>

            <div className="mt-3">
              {c.tipo === 'nota_1_5' && (
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} type="button" onClick={() => setVal(c.id, { nota: n })} className={`w-10 h-10 rounded-lg text-sm font-bold border transition ${v.nota === n ? 'bg-purple-600 border-purple-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-purple-300'}`}>{n}</button>
                  ))}
                </div>
              )}
              {c.tipo === 'escolha' && (
                <select
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-200"
                  value={v.nota != null ? String(v.nota) : ''}
                  onChange={e => setVal(c.id, { nota: e.target.value === '' ? undefined : Number(e.target.value) })}
                >
                  <option value="">Selecione...</option>
                  {(c.opcoes || []).map((op, idx) => <option key={idx} value={idx + 1}>{op}</option>)}
                </select>
              )}
              {c.tipo === 'texto' && (
                <textarea
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm min-h-[80px] resize-y focus:outline-none focus:ring-2 focus:ring-purple-200"
                  value={v.comentario || ''}
                  onChange={e => setVal(c.id, { comentario: e.target.value })}
                  placeholder="Escreva sua resposta..."
                />
              )}
            </div>

            {c.tipo !== 'texto' && (
              <input
                className="w-full mt-3 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-200"
                value={v.comentario || ''}
                onChange={e => setVal(c.id, { comentario: e.target.value })}
                placeholder="Comentário (opcional)"
              />
            )}
          </div>
        );
      })}

      {erro && <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm"><AlertCircle className="w-4 h-4" /> {erro}</div>}

      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Suas respostas são agregadas no resultado.</span>
        <button onClick={enviar} disabled={salvando} className="ml-auto px-5 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium flex items-center gap-2">
          {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar avaliação
        </button>
      </div>
    </div>
  );
};

export default Responder;
